import { reloadAppAsync } from 'expo';
import { Alert } from 'react-native';

import { AnimatedSplashScreen } from 'components/splash-screen/splash-screen';
import { isNative, isWeb } from 'utils/environment';

import crashlytics from './crashlytics';
import { SyncStorageKey, syncStorage } from './environment/sync-storage';
import logger from './logger';
import noop from './noop';

const FIVE_MINUTES = 1000 * 60 * 5;

const DO_NOT_CLEAR_ON_CRASH_SYNC_STORAGE_KEYS = [
  SyncStorageKey.LANGUAGE,
  SyncStorageKey.INTERNAL_BUILD_ONLY_ENV_PICKER_CONFIG,
  SyncStorageKey.USER_ID,
];

// Logic:
// Hit 1: restart the JS part of the app
// Hit 2: clear most local storage (within FIVE_MINUTES)
// Hit 3: full quit the app with a crash (within FIVE_MINUTES)
export const handleFatalErrorReload = () => {
  if (__DEV__) {
    reloadAppAsync();
    return; // do not clear keys in dev, very annoying
  }

  const lastErrorBoundaryCrashTime = syncStorage.getNumber(
    SyncStorageKey.LAST_ERROR_BOUNDARY_CRASH_TIME
  );
  syncStorage.setNumber(SyncStorageKey.LAST_ERROR_BOUNDARY_CRASH_TIME, Date.now());

  // Hit 3 - native crash
  const lastKeyClearTime = syncStorage.getNumber(SyncStorageKey.LAST_CRASH_KEY_CLEAR_TIME);
  if (lastKeyClearTime && Date.now() - lastKeyClearTime < FIVE_MINUTES) {
    logger.error({
      message: '[FatalErrorReload] - Doing a native crash to attempt recovery',
      isFatalErrorReload: true,
    });
    setTimeout(() => crashlytics().crash(), 500); // setTimeout give logger a change to send.
    return;
  }

  // Hit 2 - clear storage
  // clear most storage (but not user) to prevent the crash from happening if it last happened within 5 minutes...
  const shouldClearStorage =
    lastErrorBoundaryCrashTime && Date.now() - lastErrorBoundaryCrashTime < FIVE_MINUTES;
  if (shouldClearStorage) {
    syncStorage.clearAllExcept(DO_NOT_CLEAR_ON_CRASH_SYNC_STORAGE_KEYS);
    syncStorage.setNumber(SyncStorageKey.LAST_CRASH_KEY_CLEAR_TIME, Date.now());
  }

  // Hit 1, 2 - reload JS app
  logger.error({
    message: `[FatalErrorReload] - Reloading JS to attempt recovery, clearKeys:${
      shouldClearStorage || false
    }`,
    isFatalErrorReload: true,
    shouldClearStorage,
  });
  setTimeout(() => (isWeb ? window?.location?.reload() : reloadAppAsync()), 500); // setTimeout give logger a change to send.
};

const GLOBAL_ERROR_PREFIX = 'FatalGlobalHandlerError';

let previousWebOnError: OnErrorEventHandler;

export const setupGlobalErrorHandler = () => {
  if (__DEV__) {
    // this replaces the dev mode error handler which we do not want in dev mode
    return;
  }

  if (isNative) {
    ErrorUtils.setGlobalHandler((error, isFatal) => {
      handleFatalError(GLOBAL_ERROR_PREFIX, error, { isFatal });
    });
  } else {
    previousWebOnError = window.onerror;

    window.onerror = (event, source, lineno, colno, error) => {
      if (error) {
        handleFatalError(GLOBAL_ERROR_PREFIX, error, {
          web: { event, source, lineno, colno },
        });
      }
    };
  }
};

interface HandleFatalErrorOptions {
  isFatal?: boolean;
  web?: {
    event: Event | string;
    source?: string;
    lineno?: number;
    colno?: number;
  };
}

export const handleFatalError = (
  prefix: string,
  error: any,
  options: HandleFatalErrorOptions = {}
) => {
  const { isFatal, web } = options;
  // eliminate situation where app could error sooner than splash screen goes away
  AnimatedSplashScreen.hide();

  crashlytics().recordError(error);
  crashlytics().sendUnsentReports();
  const message = `[${prefix}] - ${error.message}`;
  error.message = message;
  logger.fatal({
    message,
    error,
    isFatalError: true,
    ...(isFatal !== undefined && { globalHandlerMarkedFatal: isFatal }),
  });

  if (isWeb) {
    previousWebOnError?.call(
      window,
      web?.event ?? error.message,
      web?.source,
      web?.lineno,
      web?.colno,
      error
    );
  }

  if (isNative) {
    Alert.alert(
      'Something went wrong',
      'Please reload the app.',
      [
        {
          text: 'Reload',
          onPress: handleFatalErrorReload,
          style: 'default',
        },
      ],
      {
        cancelable: false,
        onDismiss: noop,
      }
    );
  } else {
    window.alert('Something went wrong. Press OK below to reload the site.');
    window.location.reload();
  }
};
